import React, { useState, useEffect, useMemo } from "react";
import LowActivityPanel from "./LowActivityPanel";
import EmployeeHoursLookup from "./EmployeeHoursLookup";
import HealthcareEligibility from "./HealthcareEligibility";
import "./OnboardingRetention.css";

const RAILWAY = process.env.REACT_APP_API_BASE || "";
const RANGES = ["Jan–Mar", "Apr–Jun", "Jul–Sep", "Oct–Dec"];
// The report starts at Q1 2026 and measures forward; earlier hires aren't shown.
const MIN_YEAR = 2026, MIN_Q = 1;

function quarterInfo(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const q = Math.floor(d.getMonth() / 3) + 1; // 1..4
  return { key: `${y}-Q${q}`, year: y, q, label: `Q${q} ${y}`, range: RANGES[q - 1] };
}

export default function OnboardingRetention() {
  const [hires, setHires]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [selected, setSelected] = useState(null); // { title, employees }

  useEffect(() => {
    setLoading(true);
    fetch(`${RAILWAY}/api/workers/hires`)
      .then(r => r.json())
      .then(d => setHires(d.hires || []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const { quarters, noDate, earlier } = useMemo(() => {
    const byQ = {};
    let noDate = 0, earlier = 0;
    hires.forEach(h => {
      const qi = quarterInfo(h.hireDate);
      if (!qi) { noDate++; return; }
      if (qi.year < MIN_YEAR || (qi.year === MIN_YEAR && qi.q < MIN_Q)) { earlier++; return; }
      if (!byQ[qi.key]) byQ[qi.key] = { ...qi, under5: [], fivePlus: [] };
      (h.shiftCount < 5 ? byQ[qi.key].under5 : byQ[qi.key].fivePlus).push(h);
    });
    // Chronological — Q1 2026 first, then forward.
    const quarters = Object.values(byQ).sort((a, b) => a.year - b.year || a.q - b.q);
    return { quarters, noDate, earlier };
  }, [hires]);

  const openDetail = (q, bucket) => {
    const employees = [...(bucket === "under5" ? q.under5 : q.fivePlus)]
      .sort((a, b) => a.shiftCount - b.shiftCount || `${a.lastName}`.localeCompare(b.lastName));
    setSelected({
      title: `${q.label} · ${bucket === "under5" ? "worked fewer than 5 shifts" : "worked 5 or more shifts"}`,
      count: employees.length,
      employees,
    });
  };

  return (
    <div className="onboarding-retention">
      {/* ── Needs Traction (1/3) ── */}
      <div className="or-traction">
        <LowActivityPanel />
      </div>

      {/* ── Retention (2/3) ── */}
      <div className="or-main">
        <div className="or-head">
          <h2 className="or-title">Quarterly Hire Retention</h2>
          <p className="or-sub">Caregivers hired Q1 2026 onward, by hire quarter, split by how many shifts they have worked. Click a number to see who.</p>
        </div>

        {loading && <div className="or-loading"><span className="spinner" /> Loading hires…</div>}
        {error && <div className="or-error">⚠ {error}</div>}

        {!loading && !error && (
          <>
            <table className="or-table">
              <thead>
                <tr>
                  <th>Hire quarter</th>
                  <th className="num">Hires</th>
                  <th className="num">&lt; 5 shifts</th>
                  <th className="num">5+ shifts</th>
                </tr>
              </thead>
              <tbody>
                {quarters.length === 0 && (
                  <tr><td colSpan="4" className="or-empty">No hires with a hire date on file.</td></tr>
                )}
                {quarters.map(q => (
                  <tr key={q.key}>
                    <td>
                      <span className="or-q">{q.label}</span>
                      <span className="or-range">{q.range}</span>
                    </td>
                    <td className="num or-total">{q.under5.length + q.fivePlus.length}</td>
                    <td className="num">
                      <button className="or-metric under5" onClick={() => openDetail(q, "under5")} disabled={!q.under5.length}>
                        {q.under5.length}
                      </button>
                    </td>
                    <td className="num">
                      <button className="or-metric fiveplus" onClick={() => openDetail(q, "fivePlus")} disabled={!q.fivePlus.length}>
                        {q.fivePlus.length}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(earlier > 0 || noDate > 0) && (
              <div className="or-note">
                {earlier > 0 && <>{earlier} hired before Q1 2026 (not shown). </>}
                {noDate > 0 && <>{noDate} without a hire date on file (not shown).</>}
              </div>
            )}

            {/* Drill-down */}
            {selected && (
              <div className="or-detail">
                <div className="or-detail-head">
                  <span className="or-detail-title">{selected.title} <span className="or-detail-count">({selected.count})</span></span>
                  <button className="or-detail-close" onClick={() => setSelected(null)}>✕</button>
                </div>
                <div className="or-detail-list">
                  {selected.employees.map(e => (
                    <div key={e.id} className="or-emp">
                      <span className="or-emp-name">
                        {e.firstName} {e.lastName}
                        {e.role && <span className={`role-chip role-${e.role.toLowerCase()}`}>{e.role}</span>}
                      </span>
                      <span className="or-emp-shifts">{e.shiftCount} shift{e.shiftCount !== 1 ? "s" : ""}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Employee hours lookup — worked hours by year for any staff/caregiver. */}
        <EmployeeHoursLookup />

        {/* Healthcare eligibility & enrollment (ported from the standalone tracker). */}
        <HealthcareEligibility />
      </div>
    </div>
  );
}
